import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import type { z } from 'zod'
import type { organizationUpsertSchema } from '@super-mega/server/contracts/organization'
import type { AttachmentDescriptor } from '@/features/files/index.ts'

export type OperationMode = 'agent' | 'selfOperated'

export type LegalEntityFormValues = {
  operationMode: OperationMode
  name: string
  unifiedSocialCreditCode: string
  legalRepresentative?: string
  legalRepresentativeIdNumber?: string
  registeredAddress: string
  businessLicenseFileIds: number[]
  accounts: z.infer<typeof organizationUpsertSchema>['accounts']
  companyType?: string
  registeredCapital?: string
  establishedOn?: string
  validUntil?: string
  isLongTerm: boolean
  businessScope?: string
  note?: string
}

export type BusinessLicenseOcrCandidate = {
  name: string | null
  unifiedSocialCreditCode: string | null
  legalRepresentative: string | null
  registeredAddress: string | null
  companyType: string | null
  registeredCapital: string | null
  establishedOn: string | null
  validUntil: string | null
  isLongTerm: boolean
  businessScope: string | null
}

export type OrganizationDetail = {
  id: number
  operationMode: OperationMode
  name: string
  unifiedSocialCreditCode: string | null
  legalRepresentative: string | null
  legalRepresentativeIdNumber?: string | null
  registeredAddress: string
  businessLicenseFileId: number | null
  businessLicenseFile: {
    id: number
    fileName: string
    originName: string
    fileSize: string | number
    fileType: string | null
  } | null
  accounts: LegalEntityFormValues['accounts']
  companyType: string | null
  registeredCapital: string | null
  establishedOn: Date | string | null
  validUntil: Date | string | null
  isLongTerm: boolean
  businessScope: string | null
  note: string | null
}

export { INITIAL_LEGAL_ENTITY_VALUES } from '../const/legal-entity-form.model.ts'

dayjs.extend(utc)

function toDateInput(value: Date | string | null | undefined) {
  if (!value) {
    return undefined
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value
  }
  const date = dayjs.utc(value)
  return date.isValid() ? date.format('YYYY-MM-DD') : undefined
}

export function toLegalEntityFormValues(detail: OrganizationDetail): LegalEntityFormValues {
  return {
    businessLicenseFileIds: detail.businessLicenseFileId ? [detail.businessLicenseFileId] : [],
    accounts: detail.accounts,
    businessScope: detail.businessScope ?? undefined,
    companyType: detail.companyType ?? undefined,
    establishedOn: toDateInput(detail.establishedOn),
    isLongTerm: detail.isLongTerm,
    legalRepresentative: detail.legalRepresentative ?? '',
    legalRepresentativeIdNumber: detail.legalRepresentativeIdNumber ?? '',
    name: detail.name,
    note: detail.note ?? undefined,
    operationMode: detail.operationMode,
    registeredAddress: detail.registeredAddress,
    registeredCapital: detail.registeredCapital ?? undefined,
    unifiedSocialCreditCode: detail.unifiedSocialCreditCode ?? '',
    validUntil: toDateInput(detail.validUntil)
  }
}

export function getInitialBusinessLicense(detail?: OrganizationDetail): AttachmentDescriptor[] {
  if (!detail?.businessLicenseFile) {
    return []
  }
  return [
    {
      fileType: detail.businessLicenseFile.fileType,
      id: detail.businessLicenseFile.id,
      name: detail.businessLicenseFile.originName || detail.businessLicenseFile.fileName,
      size: Number(detail.businessLicenseFile.fileSize)
    }
  ]
}

export function getInitialQrFile(file: OrganizationDetail['businessLicenseFile']): AttachmentDescriptor[] {
  if (!file) {
    return []
  }
  return [{ id: file.id, name: file.originName || file.fileName, fileType: file.fileType, size: Number(file.fileSize) }]
}

export function canRecognizeBusinessLicense(file: File) {
  return ['image/jpeg', 'image/png', 'image/bmp'].includes(file.type)
}

export function hasBusinessLicenseOcrData(candidate: BusinessLicenseOcrCandidate) {
  return [
    candidate.name,
    candidate.unifiedSocialCreditCode,
    candidate.legalRepresentative,
    candidate.registeredAddress,
    candidate.companyType,
    candidate.registeredCapital,
    candidate.establishedOn,
    candidate.validUntil,
    candidate.businessScope
  ].some(Boolean)
}

export { LEGAL_ENTITY_FORM_SECTIONS } from '@/features/master-data/const/legal-entity-form.model.ts'
